import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import { Users, UserPlus, Search, ShieldPlus, ShieldMinus, Building2, Lock } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { describeApiError, mapApiError } from '../../utils/apiError';
import { companyLabel } from '../../utils/tenancy';
import { ROLE_NAMES } from '../../constants/permissions';
import { PARTNER_ADMIN_COPY, ROLE_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import PaginationBar from './PaginationBar';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;
const ALL = '';

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User ${u.id}`;

const EMPTY_FORM = { email: '', first_name: '', last_name: '' };

/**
 * A partner's people (Partner tier guide §4.6): every user of every company of
 * the partner, with its own staff shown as "Direct users". The platform adds
 * partner admins here (direct users + the partner_admin role); day-to-day user
 * management of client companies stays in the Users tab or the partner's area.
 */
const PartnerUsersTab = ({ partner, notify, onChanged }) => {
  const homeId = partner.home_company_id;
  const userErrors = [
    { status: 400, match: /last active partner admin/i, message: PARTNER_ADMIN_COPY.lastPartnerAdmin(partner.name) },
    { status: 400, match: /partner_admin can only be held/i, message: PARTNER_ADMIN_COPY.onlyDirectUsersPartnerAdmin },
    { status: 403, match: /higher privileges/i, message: PARTNER_ADMIN_COPY.higherTierUser },
  ];

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [companyFilter, setCompanyFilter] = useState(ALL);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [companies, setCompanies] = useState([]);
  const [partnerAdminRole, setPartnerAdminRole] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirm, setConfirm] = useState(null); // { user, grant: bool }
  const [busy, setBusy] = useState(false);
  const [keyReveal, setKeyReveal] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listUsers(companyFilter || null, {
        partnerId: partner.id,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data?.users || []);
      setTotal(Number.isFinite(data?.total) ? data.total : (data?.users || []).length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [partner.id, companyFilter, debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Filter options + the partner_admin role id (needed to grant it).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [companyData, roleData] = await Promise.all([
          platformAdminService.listPartnerCompanies(partner.id, { limit: 200 }),
          platformAdminService.listRoles({ includePlatformTier: true, search: ROLE_NAMES.PARTNER_ADMIN, limit: 50 }),
        ]);
        if (cancelled) return;
        setCompanies(companyData?.companies || (Array.isArray(companyData) ? companyData : []));
        const roles = Array.isArray(roleData) ? roleData : roleData?.roles || [];
        setPartnerAdminRole(roles.find((r) => r.name === ROLE_NAMES.PARTNER_ADMIN) || null);
      } catch (err) {
        if (!cancelled) notify(describeApiError(err, 'Failed to load companies/roles.'), 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partner.id, notify]);

  const isDirect = (user) => homeId != null && Number(user.company_id) === Number(homeId);
  const holdsPartnerAdmin = (user) => (user.roles || []).some((r) => r.name === ROLE_NAMES.PARTNER_ADMIN);
  const companyName = (id) =>
    companyLabel(id, companies.find((c) => c.id === id)?.name || null, { homeCompanyId: homeId });

  const handleAdd = async () => {
    if (!form.email.trim() || !partnerAdminRole) return;
    try {
      setBusy(true);
      const res = await platformAdminService.createUser({
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company_id: homeId,
        role_ids: [partnerAdminRole.id],
      });
      setAddOpen(false);
      setForm(EMPTY_FORM);
      setKeyReveal({
        apiKey: res?.api_key,
        roles: res?.roles || [],
        title: 'Partner admin added',
        subtitle: `Copy ${res?.user?.email || 'the new admin'}'s API key now — it won't be shown again.`,
      });
      await load();
      await onChanged?.();
    } catch (err) {
      notify(mapApiError(err, userErrors, 'Failed to add the partner admin.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async () => {
    if (!confirm || !partnerAdminRole) return;
    try {
      setBusy(true);
      if (confirm.grant) {
        await platformAdminService.assignUserRole(confirm.user.id, partnerAdminRole.id);
        notify(`${fullName(confirm.user)} is now a partner admin.`, 'success');
      } else {
        await platformAdminService.removeUserRole(confirm.user.id, partnerAdminRole.id);
        notify(`${fullName(confirm.user)} is no longer a partner admin.`, 'info');
      }
      setConfirm(null);
      await load();
    } catch (err) {
      notify(mapApiError(err, userErrors, 'Failed to update the role.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box className={styles.toolbar}>
        <Box className={styles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value); setOffset(0); }}
            >
              <MenuItem value={ALL}>All</MenuItem>
              {homeId != null && <MenuItem value={homeId}>{TENANCY_COPY.directUsers}</MenuItem>}
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Tooltip title={partnerAdminRole ? '' : 'The partner_admin role could not be found.'} arrow>
          <span>
            <Button
              variant="contained"
              startIcon={<UserPlus size={18} />}
              className={styles.primaryBtn}
              onClick={() => setAddOpen(true)}
              disabled={!partnerAdminRole || homeId == null}
            >
              Add partner admin
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : users.length === 0 ? (
        <Box className={styles.emptyState}>
          <Users className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No users</Typography>
          <Typography variant="body2">Add a partner admin to let the partner manage itself.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {users.map((user) => {
            const direct = isDirect(user);
            const admin = holdsPartnerAdmin(user);
            return (
              <Box key={user.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                <Box className={styles.companyMain} sx={{ flex: '1 1 220px' }}>
                  <span className={styles.companyAvatar}>
                    {(user.first_name || user.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <Box>
                    <span className={styles.companyName}>{fullName(user)}</span>
                    <span className={styles.companyMeta}>{user.email}</span>
                  </Box>
                </Box>
                <Tooltip title={direct ? TENANCY_COPY.directUsersHint(partner.name) : ''} arrow>
                  <span className={`${styles.badge} ${direct ? styles.badgeSystem : ''}`}>
                    <Building2 size={12} />
                    {companyName(user.company_id)}
                  </span>
                </Tooltip>
                <Box className={styles.roleChips} sx={{ flex: '1 1 160px' }}>
                  {admin && (
                    <Tooltip title={ROLE_COPY.partnerAdminTooltip(partner.name)} arrow>
                      <span className={`${styles.badge} ${styles.badgeAdmin}`}>{ROLE_COPY.partnerAdmin}</span>
                    </Tooltip>
                  )}
                  {(user.roles || []).some((r) => r.name === ROLE_NAMES.COMPANY_ADMIN) && (
                    <Tooltip title={ROLE_COPY.companyAdminTooltip} arrow>
                      <span className={`${styles.badge} ${styles.badgeAdmin}`}>{ROLE_COPY.companyAdmin}</span>
                    </Tooltip>
                  )}
                </Box>
                <span className={`${styles.statusPill} ${user.is_active ? styles.active : styles.inactive}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                <Box className={styles.actionsCell} sx={{ minWidth: 170 }}>
                  {direct && partnerAdminRole && (
                    <Button
                      size="small"
                      className={styles.subtleBtn}
                      startIcon={admin ? <ShieldMinus size={14} /> : <ShieldPlus size={14} />}
                      onClick={() => setConfirm({ user, grant: !admin })}
                    >
                      {admin ? 'Remove partner admin' : 'Make partner admin'}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="users" onOffsetChange={setOffset} />
      )}

      {/* Add partner admin */}
      <Dialog open={addOpen} onClose={() => !busy && setAddOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Add a partner admin</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {`Creates a direct user of ${partner.name} with the Partner admin role. ${ROLE_COPY.partnerAdminTooltip(partner.name)}`}
          </Typography>
          <TextField
            autoFocus
            label="Email"
            type="email"
            required
            fullWidth
            variant="outlined"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name"
              fullWidth
              variant="outlined"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Last name"
              fullWidth
              variant="outlined"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className={styles.formField}
            />
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setAddOpen(false)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={busy || !form.email.trim()} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Add partner admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grant / remove partner_admin */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {confirm?.grant ? 'Make partner admin' : 'Remove partner admin'}
        </DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm && fullName(confirm.user)}</Typography>
          <Typography className={styles.deleteText}>
            {confirm?.grant
              ? ROLE_COPY.partnerAdminTooltip(partner.name)
              : `They keep their account as a direct user of ${partner.name} but lose partner admin powers.`}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRoleChange}
            disabled={busy}
            className={confirm?.grant ? styles.primaryBtn : styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirm?.grant ? 'Make admin' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <ApiKeyRevealDialog
        open={!!keyReveal}
        apiKey={keyReveal?.apiKey}
        roles={keyReveal?.roles}
        title={keyReveal?.title}
        subtitle={keyReveal?.subtitle}
        onClose={() => setKeyReveal(null)}
      />
    </>
  );
};

export default PartnerUsersTab;
